import type {
  TargetareColoanaMapare,
  TargetareCriteriuFirme,
  TargetareFelCaen,
  TargetareFelServiciu,
  TargetareTipAmbiguitate,
  TargetareTipLista,
} from "@/types/targetare";

export const TIPURI_AMBIGUITATE: TargetareTipAmbiguitate[] = [
  "Neambiguu",
  "Ambiguu",
  "Ierarhic",
  "N/A",
];

export const ETICHETE_AMBIGUITATE: Record<string, string> = {
  Neambiguu: "Neambiguu",
  Ambiguu: "Ambiguu",
  Ierarhic: "Ierarhic",
  "N/A": "Fără cod CAEN",
  "(fără)": "Nedefinit",
};

export const REGULI_INCADRARE: string[] = [
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "11",
  "12",
];

export const ETICHETE_REGULA: Record<string, string> = {
  "2": "Esențială la orice dimensiune (administrație publică)",
  "3": "Esențială la orice dimensiune (servicii de încredere calificate)",
  "4": "Esențială la orice dimensiune (DNS, registre TLD)",
  "5": "Mare: esențială; altfel importantă, la orice dimensiune",
  "6": "Mijlocie/mare: esențială; mică/micro: importantă",
  "7": "Mijlocie/mare: esențială; altfel în afara scopului",
  "8": "Mare: esențială; mijlocie: importantă; altfel în afara scopului",
  "9": "Anexa I: mare esențială, mijlocie importantă",
  "11": "Anexa II: mijlocie/mare importantă",
  "12": "Anexa II, furnizori digitali: mijlocie/mare importantă",
};

export const ETICHETE_POTRIVIRE_CAEN: Record<string, string> = {
  neambiguu: "CAEN neambiguu",
  ierarhic: "CAEN ierarhic",
  ambiguu: "CAEN ambiguu",
  secundar: "CAEN secundar al serviciului",
  super_ambiguu: "CAEN super-ambiguu (orice cod autorizat)",
  principal_super_ambiguu:
    "CAEN principal super-ambiguu (istoric, până la D10)",
  secundar_super_ambiguu: "CAEN secundar super-ambiguu (istoric, până la D10)",
  principal_and_secundar_super_ambiguu:
    "CAEN principal și secundar super-ambiguu (istoric, până la D10)",
  "": "Fără potrivire CAEN",
};

export const ETICHETE_POTRIVIRE_LISTA: Record<string, string> = {
  oficiala: "Listă oficială",
  neoficiala: "Listă neoficială",
  "": "Fără listă",
};

export const ETICHETE_COLOANA_MAPARE: Record<TargetareColoanaMapare, string> = {
  CAEN_rev_2: "CAEN Rev. 2, coduri principale",
  CAEN_rev_3: "CAEN Rev. 3, coduri principale",
  caen_rev_2_secundare: "CAEN Rev. 2, coduri secundare",
  caen_rev_3_secundare: "CAEN Rev. 3, coduri secundare",
};

export const ETICHETE_FEL_CAEN: Record<TargetareFelCaen, string> = {
  principal: "CAEN principal în bilanț",
  autorizat: "cod autorizat la ONRC",
  oricare: "principal sau autorizat",
};

export const ETICHETE_FEL_SERVICIU: Record<TargetareFelServiciu, string> = {
  principal: "prin CAEN principal",
  secundar: "prin cod autorizat",
  lista: "de pe liste",
  oricare: "prin orice potrivire",
};

export const ETICHETE_TIP_LISTA: Record<TargetareTipLista, string> = {
  OFICIALA: "Oficială",
  NEOFICIALA: "Neoficială",
};

export const ETICHETE_SURSA_VERSIUNE: Record<string, string> = {
  import: "Import din fișier",
  url: "Actualizare din sursă",
  manual: "Adăugare manuală",
  crawl: "Extragere din pagină",
  text: "Text lipit",
  stergere: "Ștergere de rânduri",
};

export const ETICHETE_STARE_ACTUALIZARE: Record<string, string> = {
  "(niciodată)": "Nerulată",
  programata: "Programată",
  in_curs: "În curs",
  ok: "Reușită",
  eroare: "Eroare",
};

export const ZILE_ACTUALIZARE_LISTA = 30;

export function coloanaMapare(
  versiune: number,
  secundar: boolean
): TargetareColoanaMapare {
  if (versiune === 2) return secundar ? "caen_rev_2_secundare" : "CAEN_rev_2";
  return secundar ? "caen_rev_3_secundare" : "CAEN_rev_3";
}

export function versiuneaColoanei(coloana: TargetareColoanaMapare): number {
  return coloana === "CAEN_rev_2" || coloana === "caen_rev_2_secundare" ? 2 : 3;
}

export function codurileDinText(text: string): string[] {
  const coduri = text
    .split(/[^0-9]+/)
    .map((c) => c.trim())
    .filter((c) => c.length === 4);
  return Array.from(new Set(coduri)).sort();
}

export function listaScadenta(
  importatLa: string | null,
  zile = ZILE_ACTUALIZARE_LISTA
): boolean {
  if (!importatLa) return true;
  const t = new Date(importatLa).getTime();
  if (Number.isNaN(t)) return true;
  return Date.now() - t > zile * 24 * 3600 * 1000;
}

export function descriereCriteriu(
  c: TargetareCriteriuFirme,
  context?: { serviciu?: string | null; descriere?: string | null }
): string {
  switch (c.tip) {
    case "caen": {
      const fel = ETICHETE_FEL_CAEN[c.fel ?? "oricare"];
      const ver = c.versiune ? ` (Rev. ${c.versiune})` : "";
      const desc = context?.descriere ? ` „${context.descriere}”` : "";
      return `Firme cu CAEN ${c.cod}${ver}${desc}, ${fel}`;
    }
    case "serviciu":
      return `Firme potrivite pe serviciul ${context?.serviciu ?? c.entity_id} ${ETICHETE_FEL_SERVICIU[c.fel ?? "oricare"]}`;
    case "lista":
      return `Firmele de pe lista „${c.nume_fisier}”`;
    case "ambiguitate":
      return `Firme potrivite pe servicii cu ambiguitate „${ETICHETE_AMBIGUITATE[c.valoare] ?? c.valoare}”`;
    case "scor":
      return c.min !== undefined && c.max !== undefined && c.min === c.max
        ? `Firme cu scorul de potrivire ${c.min}`
        : `Firme cu scorul de potrivire între ${c.min ?? 0} și ${c.max ?? 100}`;
    case "sector":
      return `Firme din sectorul ${c.nume}`;
    case "cui":
      return `${c.cui.length} firme selectate`;
    default:
      return "Firme";
  }
}

export function numeFisierDinSursa(
  nume: string,
  foaie?: string | null
): string {
  const baza = nume
    .replace(/\.(xlsx|xls|csv|pdf|html?|txt|docx?)$/i, "")
    .trim();
  return foaie ? `${baza} [${foaie}].csv` : `${baza}.csv`;
}

export function campuriDinText(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  text.split(/\r?\n/).forEach((linie) => {
    const i = linie.indexOf("=");
    if (i <= 0) return;
    const cheie = linie.slice(0, i).trim();
    if (cheie) out[cheie] = linie.slice(i + 1).trim();
  });
  return out;
}

export function textDinCampuri(campuri: Record<string, string> | null): string {
  if (!campuri) return "";
  return Object.entries(campuri)
    .map(([k, v]) => `${k}=${v}`)
    .join("\n");
}
